import {
  DateTimeRangeFilter,
  TableActionMenu,
  UuidFilterInput,
  type TableActionItem,
} from '@/components/shared';
import type { ProjectStatus } from '../types';

const STATUS_OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export interface ProjectsFiltersProps {
  status: ProjectStatus | '';
  organizationId: string;
  owner: string;
  updatedFrom: string;
  updatedTo: string;
  includeAllWithAccess?: boolean;
  actions?: TableActionItem[];
  showOrganizationFilter?: boolean;
  showOwnerFilter?: boolean;
  showAccessFilter?: boolean;
  onStatusChange: (status: ProjectStatus | '') => void;
  onOrganizationIdChange: (value: string) => void;
  onOwnerChange: (value: string) => void;
  onUpdatedRangeChange: (updatedFrom: string, updatedTo: string) => void;
  onIncludeAllWithAccessChange?: (value: boolean) => void;
}

export function ProjectsFilters({
  status,
  organizationId,
  owner,
  updatedFrom,
  updatedTo,
  includeAllWithAccess = false,
  actions = [],
  showOrganizationFilter = true,
  showOwnerFilter = true,
  showAccessFilter = false,
  onStatusChange,
  onOrganizationIdChange,
  onOwnerChange,
  onUpdatedRangeChange,
  onIncludeAllWithAccessChange,
}: ProjectsFiltersProps) {
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-4">
      <label className="flex min-w-[160px] flex-col gap-1 text-small font-medium text-neutral-700">
        Status
        <select
          value={status}
          onChange={(event) => onStatusChange(event.target.value as ProjectStatus | '')}
          className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {showOrganizationFilter && (
        <UuidFilterInput
          label="Organization"
          value={organizationId}
          onChange={onOrganizationIdChange}
          placeholder="Organization UUID"
          className="flex min-w-[260px] flex-col gap-1 text-small font-medium text-neutral-700"
        />
      )}

      {showOwnerFilter && (
        <label className="flex min-w-[260px] flex-col gap-1 text-small font-medium text-neutral-700">
          Owner
          <input
            type="text"
            value={owner}
            onChange={(event) => onOwnerChange(event.target.value.trim())}
            placeholder="User UUID or email"
            className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
          />
        </label>
      )}

      <DateTimeRangeFilter
        label="Updated range"
        from={updatedFrom}
        to={updatedTo}
        onRangeChange={onUpdatedRangeChange}
      />

      {showAccessFilter && (
        <label className="flex h-10 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 text-small font-medium text-neutral-700">
          <input
            type="checkbox"
            checked={includeAllWithAccess}
            onChange={(event) => onIncludeAllWithAccessChange?.(event.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-backoffice-500 focus:ring-backoffice-500"
          />
          include all with access
        </label>
      )}

      {actions.length > 0 && (
        <div className="ml-auto flex min-w-[2.5rem] justify-end">
          <TableActionMenu actions={actions} label="Study table actions" />
        </div>
      )}
    </div>
  );
}
